/**
 * permissions.ts
 *
 * Legacy permission IDs (`users_permissions.permissionID`) referenced by name.
 * The numbers are a fixed contract with the v4 DB — never renumber them. The
 * labels staff see when granting live in `pages/admin/UsersAdmin.tsx` (and in
 * corporate-dashboard's `components/users-table.tsx`); keep both in sync when
 * adding an ID here.
 *
 * THIS MAP IS AN ALIAS LIST, NOT THE REGISTER. It holds only the IDs some
 * TypeScript file needs by name, so an ID missing from here says nothing about
 * whether the permission exists — `UsersAdmin.tsx` is the register. Reading this
 * file as the full set is how Reports (15) came to be granted for years while
 * gating nothing.
 */
export const PERMISSION = {
  /**
   * Reports — the gate on everything in /reports EXCEPT My Reports and History,
   * which stay open to any user of the reports module. Deliberately partial: a
   * user without this can still reach the reports they own or that were shared
   * with them, and their run history; they cannot reach Standard Reports,
   * Scheduled Reports, Presets or the builder.
   */
  REPORTS: 15,
  /**
   * Consult Portal — the gate on the Consults worklist. Legacy used it only to
   * show the Consults link (html/html/header-footer/side-nav2.php:120-122);
   * ConsultsController checks it on the list and ListExportsController on the
   * export.
   */
  CONSULTS: 5,
  /**
   * Surgery — the gate on the Surgery worklist and the patient chart's Surgery
   * tab. Legacy calls this `$consults_access` (api/v4.2/navigation-main.php:36,
   * header-footer/side-nav.php:217) while labelling every visible control
   * "Surgery". Independent of the per-account `surgery` module: the module says
   * the practice has Surgery at all, this says the staff member may open it, and
   * both must pass.
   */
  SURGERY: 6,
  /**
   * Delete Products — removing a product from a patient's available or on-hold
   * stock, and deleting a product from the catalogue. Level 4 clears the
   * patient-side delete without it; the catalogue delete clears at level 3.
   * Consuming and returning a patient's product are deliberately NOT gated.
   */
  DELETE_PRODUCTS: 19,
  /**
   * Manage Store Availability — adding a product to the store or removing it
   * (Admin > Products, "Available in Store"). Level 4 clears it without the
   * grant; ProductsController::update enforces the same rule server-side.
   */
  MANAGE_STORE_AVAILABILITY: 35,
  /** Marketing Access — the gate on the whole Marketing section. */
  MARKETING: 20,
  /** Email Editor Full Access — unlocks the DnD editor beyond the locked view. */
  EMAIL_EDITOR_FULL: 21,
  /** Marketing Campaigns — build/send email + SMS campaigns. */
  MARKETING_CAMPAIGNS: 27,
  /** Marketing Automations — build/run email + SMS automations. */
  MARKETING_AUTOMATIONS: 28,
  /** Marketing Push Notifications — build/send push campaigns. */
  MARKETING_PUSH: 29,
  /** Marketing App Posts — manage in-app posts. */
  MARKETING_APP_POSTS: 30,
  /** Marketing Settings — senders, from-numbers, compliance. */
  MARKETING_SETTINGS: 31,
  /**
   * Apple TV Signage — manage what plays on the practice's waiting-room
   * screens: locations, screens and groups, playlists, media, and pairing a
   * new Apple TV. Deliberately separate from Marketing Campaigns: sending a
   * campaign and changing a screen in the waiting room are different trusts.
   */
  APPLE_TV: 32,
  /**
   * Payouts — the Forward deposits landing in the practice's bank account, and
   * the payments that make up each one. Separate from Disputes below: seeing
   * what the practice was paid and answering a chargeback are different jobs,
   * and a bookkeeper usually needs the first without the second.
   */
  PAYOUTS: 33,
  /** Disputes — card chargebacks raised against the practice's payments. */
  DISPUTES: 34,
  /**
   * Post Op Portal — legacy `$postops_access`
   * (html/html/api/v4.2/navigation-main.php:37). Gates the Post-ops
   * worklist and the patient-chart Post-ops tab.
   *
   * Independent of the per-account `post ops` module: BOTH must pass.
   * Grantable from the V5 User Permissions modal (pages/admin/permissionsCatalog.ts)
   * as well as corp-dashboard; UsersAdmin.permissions.test fails if it is hidden
   * or removed there.
   */
  POST_OPS: 7,
  /**
   * Admin Access — the gate on the whole Admin area, in place of the old
   * level-3 / level-4 checks. Level 5 passes without it (`useAuth().hasAdminAccess`).
   * Not 35: that is Manage Store Availability.
   */
  ADMIN_ACCESS: 36,
  /**
   * User Audit — the gate on Admin > Users > User Audit and its CSV download.
   * Works only for level 3 and above, and the server checks the live level and
   * this grant on every request. Nobody holds it by default and new users are
   * not given it. Pinned to UserAuditAccess::PERMISSION_USER_AUDIT by
   * pages/admin/userAuditPermission.test.ts.
   */
  USER_AUDIT: 37,
} as const;

/**
 * Who may enter the Admin area: level 5 always, otherwise only a holder of
 * Admin Access. User level below 5 grants nothing on its own. Shared by the
 * nav filter and the route guards so the two cannot disagree.
 *
 * Deliberately NOT `isAdmin` (level >= 3), which still gates features outside
 * Admin — inventory, leads, schedule — and must keep meaning what it means.
 */
export function hasAdminAccess(userLevel: number, permissions: readonly number[] = []): boolean {
  return userLevel >= 5 || permissions.includes(PERMISSION.ADMIN_ACCESS);
}
